import * as React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as DocIcon,
} from '@mui/icons-material';
import { useStyles } from './Upload.styles';
import { AttachStatus } from '../../containers/Request/RequestForm';

// Enhanced interface with better configuration
export interface IUploadProps {
  selectedFiles: File[];
  onFileChange: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onClearFiles: () => void;
  attachmentStatus: AttachStatus;
  errorText: string;
  // New configuration props
  maxFileSize?: number; // in bytes
  maxFileCount?: number;
  acceptedFileTypes?: string[];
  showProgress?: boolean;
  showFileSize?: boolean;
  disabled?: boolean;
}

// Configuration constants
const DEFAULT_CONFIG = {
  acceptedFileTypes: [
    '.bmp',
    '.csv',
    '.doc',
    '.docx',
    '.gif',
    '.jpg',
    '.jpeg',
    '.pdf',
    '.png',
    '.pps',
    '.ppt',
    '.pptx',
    '.rtf',
    '.tif',
    '.tiff',
    '.txt',
    '.xls',
    '.xlsm',
    '.xlsx',
    '.zip',
  ],
  // 1GB
  maxFileCount: 10,
  maxFileSize: 1024 * 1024 * 1024,
};

// File validation types
interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const Upload = React.memo<IUploadProps>((props) => {
  const {
    selectedFiles,
    onFileChange,
    onRemoveFile,
    onClearFiles,
    attachmentStatus,
    errorText,
    maxFileSize = DEFAULT_CONFIG.maxFileSize,
    maxFileCount = DEFAULT_CONFIG.maxFileCount,
    acceptedFileTypes = DEFAULT_CONFIG.acceptedFileTypes,
    showProgress = false,
    showFileSize = true,
    disabled = false,
  } = props;

  const classes = useStyles();

  const [dragActive, setDragActive] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
  const [uploadProgress] = React.useState(0);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Clear validation errors when files are cleared externally (e.g., form submission)
  // or re-validate when files are removed to clear stale "already selected" errors
  React.useEffect(() => {
    if (selectedFiles.length === 0 && validationErrors.length > 0) {
      // Clear all validation errors when no files are selected
      setValidationErrors([]);
    } else if (validationErrors.length > 0) {
      // Re-validate to remove any "File already selected" errors for files that were removed
      const currentFileErrors = validationErrors.filter((error) =>
        selectedFiles.some((file) => error.includes(file.name))
      );
      if (currentFileErrors.length !== validationErrors.length) {
        setValidationErrors(currentFileErrors);
      }
    }
  }, [selectedFiles, validationErrors]);

  // Memoized accepted file types string
  const acceptedTypesString = React.useMemo(
    () => acceptedFileTypes.join(','),
    [acceptedFileTypes]
  );

  // Format file size helper
  const formatFileSize = React.useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // File validation function
  const validateFiles = React.useCallback(
    (files: File[]): ValidationResult => {
      const errors: string[] = [];

      // Check file count
      if (selectedFiles.length + files.length > maxFileCount) {
        errors.push(`Maximum ${maxFileCount} files allowed`);
      }

      files.forEach((file, index) => {
        // Check file size
        if (file.size > maxFileSize) {
          errors.push(
            `${file.name}: File size exceeds ${formatFileSize(maxFileSize)}`
          );
        }

        // Check file type
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!acceptedFileTypes.includes(fileExtension)) {
          errors.push(`${file.name}: File type not supported`);
        }

        // Check for duplicates
        if (
          selectedFiles.some(
            (existing) =>
              existing.name === file.name && existing.size === file.size
          )
        ) {
          errors.push(`${file.name}: File already selected`);
        }
      });

      return { errors, valid: errors.length === 0 };
    },
    [
      selectedFiles,
      maxFileSize,
      maxFileCount,
      acceptedFileTypes,
      formatFileSize,
    ]
  );

  // Get file icon based on file type
  const getFileIcon = React.useCallback((fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'tif':
      case 'tiff':
        return <ImageIcon color="info" />;
      case 'xls':
      case 'xlsx':
      case 'xlsm':
      case 'csv':
        return <ExcelIcon color="success" />;
      case 'doc':
      case 'docx':
      case 'rtf':
      case 'txt':
        return <DocIcon color="primary" />;
      default:
        return <FileIcon />;
    }
  }, []);

  // Enhanced file change handler with validation
  const handleFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);
        const validation = validateFiles(files);

        if (validation.valid) {
          onFileChange(files);
          setValidationErrors([]);
        } else {
          setValidationErrors(validation.errors);
        }

        // Reset input to allow selecting the same file again
        e.target.value = '';
      }
    },
    [validateFiles, onFileChange]
  );

  // Enhanced drag handlers with validation
  const handleDragOver = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setDragActive(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
    },
    []
  );

  const handleDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (
        !disabled &&
        e.dataTransfer.files &&
        e.dataTransfer.files.length > 0
      ) {
        const files = Array.from(e.dataTransfer.files);
        const validation = validateFiles(files);

        if (validation.valid) {
          onFileChange(files);
          setValidationErrors([]);
        } else {
          setValidationErrors(validation.errors);
        }
      }
    },
    [disabled, validateFiles, onFileChange]
  );

  // Click handler for file input
  const handleClick = React.useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  // Keyboard handler for accessibility
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault();
        fileInputRef.current?.click();
      }
    },
    [disabled]
  );

  // Clear files with confirmation
  const handleClearFiles = React.useCallback(() => {
    onClearFiles();
    setValidationErrors([]);
  }, [onClearFiles]);

  return (
    <Box className={classes.container}>
      <Paper elevation={2} className={classes.paper}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          File Upload
          {attachmentStatus === AttachStatus.Required && (
            <Typography
              component="span"
              color="error"
              className={classes.requiredIndicator}
            >
              *
            </Typography>
          )}
        </Typography>

        <Box className={classes.contentContainer}>
          {/* Upload Area */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-label="Upload files by clicking or dragging"
            className={`${classes.dragBox} ${dragActive ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
          >
            <input
              type="file"
              multiple
              accept={acceptedTypesString}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="upload-input"
              ref={fileInputRef}
              disabled={disabled}
              aria-label="File upload input"
            />
            <CloudUploadIcon
              className={`${classes.uploadIcon} ${dragActive ? 'active' : 'inactive'}`}
            />
            <Typography variant="body2" className={classes.uploadText}>
              {dragActive ? 'Drop files here' : 'Click or drag files here'}
            </Typography>
            <Typography variant="caption" className={classes.caption}>
              Max file size: {formatFileSize(maxFileSize)} | Max files:{' '}
              {maxFileCount}
            </Typography>
            <Typography
              variant="caption"
              className={classes.supportedTypesText}
            >
              Supported: {acceptedFileTypes.join(', ')}
            </Typography>
          </Box>

          {/* File List */}
          {selectedFiles.length > 0 && (
            <Box className={classes.fileListContainer}>
              {/* Header with count and clear button */}
              <Box className={classes.fileListHeader}>
                <Chip
                  label={`${selectedFiles.length} file(s) selected`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFiles}
                  disabled={disabled}
                  title="Remove all files"
                >
                  Clear All
                </Button>
              </Box>

              {/* Progress bar if enabled */}
              {showProgress && uploadProgress > 0 && (
                <Box className={classes.progressContainer}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                  <Typography
                    variant="caption"
                    className={classes.progressText}
                  >
                    {uploadProgress}% uploaded
                  </Typography>
                </Box>
              )}

              {/* File list */}
              <List dense className={classes.fileList}>
                {selectedFiles.map((file, idx) => (
                  <ListItem
                    key={`${file.name}-${file.size}-${idx}`}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label={`delete ${file.name}`}
                        onClick={() => onRemoveFile(idx)}
                        disabled={disabled}
                        size="small"
                        title={`Remove ${file.name}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                    className={classes.fileListItem}
                  >
                    <ListItemIcon className={classes.fileIcon}>
                      {getFileIcon(file.name)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          className={classes.fileName}
                          title={file.name}
                        >
                          {file.name}
                        </Typography>
                      }
                      secondary={
                        showFileSize ? formatFileSize(file.size) : undefined
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>

        {/* Error Messages */}
        {(errorText || validationErrors.length > 0) && (
          <Box className={classes.errorContainer}>
            {errorText && (
              <Alert severity="error" className={classes.errorAlert}>
                {errorText}
              </Alert>
            )}
            {validationErrors.map((error, index) => (
              <Alert
                key={index}
                severity="warning"
                className={classes.errorAlert}
              >
                {error}
              </Alert>
            ))}
          </Box>
        )}

        {/* Help text */}
        <Typography
          variant="caption"
          color="text.secondary"
          className={classes.helpText}
        >
          💡 Tip: You can select multiple files or drag and drop them here
        </Typography>
      </Paper>
    </Box>
  );
});

Upload.displayName = 'Upload';

export default Upload;
